import { PencilIcon, Trash2Icon } from 'lucide-react'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'

import { extractStorageResources } from '@campai/lib-common/entities'
import { formatDateTime } from '@campai/lib-common/i18n'
import { Actor, ActorName, Markdown } from '@campai/lib-web/components'
import { cn } from '@campai/lib-web/components/new/lib'
import { Button, Card, CardHeader } from '@campai/lib-web/components/new/ui'
import { type RouterOutput } from '@campai/lib-web/utils/trpc'

import { AttachmentList, type AttachmentInput } from '@/product/community/shared/Attachments'
import { InlineContentComposer } from '@/product/community/shared/InlineContentComposer'
import { isOwnFeedbackContent } from '@/product/feedback/access'

type CommentData = RouterOutput['product']['feedback']['comments']['listComments']['comments'][number]

type CommentProps = {
    comment: CommentData
    canManage: boolean
    editingComment: CommentData | null
    targetCommentId: string | null
    updateComment: (comment: CommentData, input: { message: string; attachments: AttachmentInput }) => Promise<void>
    setEditingComment: (comment: CommentData | null) => void
    setCommentToDelete: (comment: CommentData) => void
}

export const Comment = observer(
    ({
        comment,
        canManage,
        editingComment,
        targetCommentId,
        updateComment,
        setEditingComment,
        setCommentToDelete,
    }: CommentProps) => {
        const { i18n, t } = useTranslation()
        const commentId = comment._id.toString()
        const canEditComment = canManage || isOwnFeedbackContent(comment.createdBy)
        const canDeleteComment = canEditComment
        const commentCreatedAt = comment.createdAt ? formatDateTime(comment.createdAt, { i18n }) : ''
        const isEditingComment = editingComment?._id.toString() === commentId

        if (isEditingComment) {
            return (
                <InlineContentComposer
                    initialMessage={comment.message}
                    initialAttachments={extractStorageResources(comment.attachments)}
                    actor={comment.createdBy!}
                    authorDescription={commentCreatedAt}
                    messageLabel={t('product.feedback.ticket.commentDialog.message')}
                    submitLabel={t('common.save')}
                    cancelLabel={t('common.cancel')}
                    onSubmit={(input) => updateComment(comment, input)}
                    onCancel={() => setEditingComment(null)}
                />
            )
        }

        return (
            <Card
                id={`comment-${commentId}`}
                className={cn('scroll-mt-24 gap-0 p-0 transition-colors', {
                    'border-primary bg-primary/5 ring-primary/30 ring-2': targetCommentId === commentId,
                })}
            >
                <CardHeader className="gap-3 p-5">
                    <div className="flex items-start gap-4">
                        <Actor size="lg" actor={comment.createdBy!} />
                        <div className="min-w-0 flex-1 space-y-2">
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="truncate font-medium">
                                        <ActorName actor={comment.createdBy!} />
                                    </div>
                                    {commentCreatedAt && (
                                        <div className="text-muted-foreground text-sm">{commentCreatedAt}</div>
                                    )}
                                </div>
                                <div className="flex shrink-0 items-center gap-1">
                                    {canEditComment && (
                                        <Button
                                            variant="ghost"
                                            size="icon-sm"
                                            title={t('product.feedback.ticket.comment.edit')}
                                            onClick={() => setEditingComment(comment)}
                                        >
                                            <PencilIcon />
                                        </Button>
                                    )}
                                    {canDeleteComment && (
                                        <Button
                                            variant="ghost"
                                            size="icon-sm"
                                            title={t('product.feedback.ticket.comment.delete')}
                                            onClick={() => setCommentToDelete(comment)}
                                        >
                                            <Trash2Icon />
                                        </Button>
                                    )}
                                </div>
                            </div>
                            <div>
                                <Markdown className="text-sm leading-relaxed" syntaxHighlighting>
                                    {comment.message}
                                </Markdown>
                                <AttachmentList
                                    markdown={comment.message}
                                    attachments={extractStorageResources(comment.attachments)}
                                />
                            </div>
                        </div>
                    </div>
                </CardHeader>
            </Card>
        )
    }
)
